import { z } from 'zod';
/*
  GlobalRole defines the available global roles (cross-organization).
*/
export var GlobalRole;
(function (GlobalRole) {
    GlobalRole["User"] = "user";
    GlobalRole["Admin"] = "kirin_admin";
})(GlobalRole || (GlobalRole = {}));
/*
  OrganizationRole defines the available roles a user can have within an organization.
*/
export var OrganizationRole;
(function (OrganizationRole) {
    OrganizationRole["Owner"] = "owner";
    OrganizationRole["Manager"] = "manager";
    OrganizationRole["Member"] = "member";
    OrganizationRole["Support"] = "support";
})(OrganizationRole || (OrganizationRole = {}));
/*
  organizationRoleOrder defines the priority/order of organization roles.
  Lower numbers indicate higher priority.
  Used for determining the highest role when a user has multiple roles.
*/
export const organizationRoleOrder = {
    [OrganizationRole.Owner]: 0,
    [OrganizationRole.Manager]: 1,
    [OrganizationRole.Member]: 2,
    [OrganizationRole.Support]: 3,
};
/*
  organizationRoleSchema is the Zod schema for validating organization roles.
  In Zod v4, z.enum() works directly with TypeScript enums.
*/
export const organizationRoleSchema = z.enum(OrganizationRole);
/*
  InviteAction defines the available actions for handling organization invites.
*/
export var InviteAction;
(function (InviteAction) {
    InviteAction["Accept"] = "accept";
    InviteAction["Reject"] = "reject";
})(InviteAction || (InviteAction = {}));
/*
  inviteActionSchema is the Zod schema for validating invite actions.
  In Zod v4, z.enum() works directly with TypeScript enums.
*/
export const inviteActionSchema = z.enum(InviteAction);
/*
  handleInviteRequestSchema is the schema for handling an organization invite.
*/
export const handleInviteRequestSchema = z.object({
    action: inviteActionSchema,
});
/*
  Token-based invite response (KIR-3786). The token carried in the invite email
  link is the source of truth for org placement — the accepting session's identity
  is irrelevant. Used by the InviteGuard deep-link flow for both accept and reject.
*/
export const respondToInviteByTokenRequestSchema = z.object({
    token: z.string().min(1, 'Invite token cannot be empty'),
    action: inviteActionSchema,
});
export const respondToInviteByTokenResponseSchema = z.object({
    // Present on accept (the org the user was placed into); null on reject.
    organizationId: z.string().nullable(),
});
/*
  inviteTokenInfoSchema is returned when resolving an invite token, so the UI can
  show the org name in the approve modal without depending on the user's org list
  (which may not include the invite when the Descope identity differs).
*/
export const inviteTokenInfoSchema = z.object({
    organizationId: z.string(),
    organizationName: z.string(),
    role: organizationRoleSchema,
    /*
      Whether the *requesting* session is the identity this invite was issued to,
      i.e. whether accept/reject would succeed (KIR-4166). Advisory UI state only —
      the respond endpoint re-checks and stays the enforcement point. When false
      the UI shows a dismissible "this invite isn't for this account" state rather
      than Accept/Reject buttons that are guaranteed to 403.
  
      Optional + defaulted so a frontend built against this contract still parses a
      response from a backend that predates these fields (rolling deploy). Without
      that tolerance the whole modal disappears against an older API, because
      parseSchema rejects the response outright. The default is the pre-existing
      behaviour: assume the invite is answerable and let the respond call decide.
    */
    canRespond: z.boolean().optional().default(true),
    /* Address the invite was issued to, so the UI can name the account to sign in as. */
    invitedEmail: z.string().nullable().optional().default(null),
});
/*
  Error codes on the 403 from POST /account/invites/respond. Both mean "you may
  not answer this invite", but they differ in whether trying again could ever
  work — and the UI must not treat them alike, because it discards the pending
  invite token when it decides a response is hopeless (KIR-4166).

  Shared here so the backend's `createError` code and the frontend's terminal
  check can never drift apart.
*/
export const InviteErrorCode = {
    /** Definitive: the signed-in identity is verifiably not the invitee. Terminal. */
    EmailMismatch: 'INVITE_EMAIL_MISMATCH',
    /**
     * Recoverable: the responding identity could not be confirmed — a Descope
     * lookup blip, or an email the user has not verified yet. Retrying (or
     * verifying the address) can succeed, so this must stay non-terminal.
     */
    IdentityUnverified: 'INVITE_IDENTITY_UNVERIFIED',
};
/*
  organizationIdSchema validates an organizationId parameter.
  It must be a URL-safe string containing only alphanumeric characters, hyphens, and underscores.
  Example: "org-abc123" or "tenant_456xyz"
*/
export const organizationIdSchema = z
    .string()
    .min(1, 'Organization ID cannot be empty')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Organization ID must be URL-safe (alphanumeric, hyphens, underscores only)');
/*
  newOrganizationSchema is the schema for creating a new organization.
*/
export const newOrganizationSchema = z.object({
    name: z.string().min(1).max(254),
});
/*
  sparseOrganizationSchema is the schema for getting an organization with a minimal set of fields.
  this can be used in organization endpoints or in other contexts where the organization is needed inside another object.
*/
export const sparseOrganizationSchema = newOrganizationSchema.extend({
    id: z.string(),
    isInvited: z.boolean().optional(), // True if user has a pending invite to this organization
    createdAt: z.coerce.date().optional(), // Organization creation date, used for dashboard time range limits
});
export const updateOrganizationNameSchema = z.object({
    name: newOrganizationSchema.shape.name,
    id: organizationIdSchema,
});
/*
  organizationListSchema is the schema for listing organizations.
*/
export const organizationListSchema = z.array(sparseOrganizationSchema);
/*
  Organization handshake schema - used by MDM/agent install
*/
export const organizationHandshakeRequestSchema = z.object({
    userEmail: z.string().email(),
    deviceId: z.string().optional(),
});
export const organizationHandshakeResponseSchema = z.object({
    userApiKey: z.string(),
    userId: z.string(),
    organizationId: organizationIdSchema,
});
//# sourceMappingURL=organizations.js.map